<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import CalendarModal from "@/components/CalendarModal.vue";
import type { TicketDateOption } from "@/types";

const props = defineProps<{
  dates: TicketDateOption[];
  modalDates: TicketDateOption[];
  selectedDateId: string;
  formatPrice: (value: number) => string;
}>();

const emit = defineEmits<{
  select: [dateId: string];
}>();

const isCalendarModalOpen = ref(false);
const calendarScroll = ref<HTMLElement | null>(null);

const monthGroups = computed(() =>
  props.dates.reduce<
    Array<{ id: string; month: string; dates: TicketDateOption[] }>
  >((groups, date) => {
    const lastGroup = groups[groups.length - 1];

    if (lastGroup?.month === date.month) {
      lastGroup.dates.push(date);
      return groups;
    }

    groups.push({
      id: date.id,
      month: date.month,
      dates: [date],
    });

    return groups;
  }, []),
);

function selectDate(dateId: string) {
  emit("select", dateId);
}

watch(
  [() => props.selectedDateId, () => props.dates],
  () => {
    void scrollSelectedDateIntoView();
  },
  { flush: "post" },
);

onMounted(() => {
  void scrollSelectedDateIntoView("auto");
});

async function scrollSelectedDateIntoView(
  behavior: ScrollBehavior = "smooth",
) {
  await nextTick();

  const container = calendarScroll.value;

  if (!container || !props.selectedDateId) {
    return;
  }

  const target = container.querySelector<HTMLElement>(
    `[data-ticket-date-id="${props.selectedDateId}"]`,
  );

  if (!target) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const targetOffset = targetRect.left - containerRect.left;
  const centeredLeft =
    container.scrollLeft +
    targetOffset -
    (container.clientWidth - target.offsetWidth) / 2;

  container.scrollTo({
    left: Math.max(centeredLeft, 0),
    behavior,
  });
}
</script>

<template>
  <section
    class="relative min-w-0 rounded-[20px] lg:rounded-[40px] bg-white p-3 lg:p-8 shadow-widget"
  >
    <button
      class="absolute right-2 top-1 lg:right-7 lg:top-6 z-[4] grid h-9 w-9 cursor-pointer place-items-center rounded-full text-ink transition hover:bg-ink/5 active:bg-ink/10"
      type="button"
      aria-label="Открыть календарь"
      @click="isCalendarModalOpen = true"
    >
      <svg class="h-6 w-6 min-w-6" aria-hidden="true">
        <use href="#ticket-widget-icon-calendar" />
      </svg>
    </button>

    <div
      ref="calendarScroll"
      class="flex w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div
        v-for="group in monthGroups"
        :key="group.id"
        class="grid flex-none min-w-max gap-y-3"
      >
        <p
          class="sticky left-0 z-[2] m-0 w-max min-w-0 bg-white pr-2 text-base font-normal leading-5 text-ink"
        >
          {{ group.month }}
        </p>

        <div class="flex gap-1 lg:gap-2 pr-2">
          <button
            v-for="date in group.dates"
            :key="date.id"
            :data-ticket-date-id="date.id"
            class="h-[74px] lg:h-[138px] w-[44px] lg:w-[88px] shrink-0 rounded-[10px] lg:rounded-2xl border p-2 lg:p-4 text-center transition cursor-pointer"
            :class="
              date.id === selectedDateId
                ? 'border-brand bg-brand text-darkwhite/70'
                : 'border-brand/20 bg-white text-ink/80 hover:border-brand'
            "
            type="button"
            @click="selectDate(date.id)"
          >
            <span
              class="block text-[10px] lg:text-base leading-none font-semibold"
              :class="
                date.isWeekend && date.id !== selectedDateId
                  ? 'text-darkred'
                  : ''
              "
            >
              {{ date.weekday }}
            </span>
            <span
              class="mt-1 lg:mt-4 block text-base lg:text-2xl font-semibold leading-5 lg:leading-7"
              :class="date.id === selectedDateId ? 'text-white' : 'text-ink'"
            >
              {{ date.day }}
            </span>
            <span
              class="mx-auto mt-1.5 lg:mt-3 block h-px w-full"
              :class="date.id === selectedDateId ? 'bg-white' : 'bg-ink/20'"
            />
            <span
              class="mt-1.5 lg:mt-3 flex min-h-[10px] lg:min-h-5 justify-center whitespace-nowrap text-[8px] lg:text-base text-center font-normal leading-[10px] lg:leading-5"
              :class="
                date.id === selectedDateId ? 'text-darkwhite/70' : 'text-ink/40'
              "
            >
              {{ date.price === null ? "" : formatPrice(date.price) }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <CalendarModal
      v-if="isCalendarModalOpen"
      :dates="modalDates"
      :selected-date-id="selectedDateId"
      :format-price="formatPrice"
      @select="selectDate"
      @close="isCalendarModalOpen = false"
    />
  </section>
</template>
